import { TaskStatus } from "api/model";
import {
  getAnalyzeCanopyForTrial,
  getAnalyzeEmergenceForTrial,
  type OrthoWorkflow,
  type TrialPlotTask,
} from "context/OrthoWorkflow";

export type AnalysisKind = "canopy" | "emergence";

export const ANALYSIS_KINDS: readonly AnalysisKind[] = ["canopy", "emergence"];

/** Identifies one analysis of one ortho, for selection and queueing. */
export const getAnalysisId = (workflowKey: string, kind: AnalysisKind) =>
  `${workflowKey}|${kind}`;

const getAnalysisTask = (
  workflow: OrthoWorkflow | undefined,
  trialId: number,
  kind: AnalysisKind,
): TrialPlotTask =>
  kind === "canopy"
    ? getAnalyzeCanopyForTrial(workflow, trialId)
    : getAnalyzeEmergenceForTrial(workflow, trialId);

export interface AnalysisCellPlan {
  task: TrialPlotTask;
  /** Why the analysis failed or couldn't start; null if nothing went wrong. */
  errorMessage: string | null;
  /** Can be picked to run: it hasn't run, failed, or completed (to re-run). */
  isSelectable: boolean;
  /** Failed or completed: selecting it again is a deliberate retry, so it starts unselected. */
  needsOptIn: boolean;
}

/** What running `kind` on this workflow's ortho would involve for `trialId`. */
export const getAnalysisCellPlan = (
  workflow: OrthoWorkflow | undefined,
  trialId: number,
  kind: AnalysisKind,
): AnalysisCellPlan => {
  const task = getAnalysisTask(workflow, trialId, kind);
  const isCompleted = task.status === TaskStatus.completed;
  const errorMessage = task.startError?.message ?? task.failureMessage;
  return {
    errorMessage,
    isSelectable: task.canStart || task.canRetry || isCompleted,
    needsOptIn: errorMessage != null || isCompleted,
    task,
  };
};

/** Whether the cell is ticked: the default for its state, flipped if the user toggled it. */
export const isAnalysisSelected = (
  plan: Pick<AnalysisCellPlan, "needsOptIn">,
  isToggled: boolean,
): boolean => plan.needsOptIn === isToggled;

export interface AnalysisSelectionEntry {
  id: string;
  plan: Pick<AnalysisCellPlan, "isSelectable" | "needsOptIn">;
}

/**
 * The toggled ids after selecting every pickable analysis that hasn't been
 * ticked, leaving failed and completed ones (which need an opt-in) alone.
 */
export const selectAllAnalyses = (
  entries: readonly AnalysisSelectionEntry[],
  toggled: ReadonlySet<string>,
): ReadonlySet<string> => {
  const next = new Set(toggled);
  for (const { id, plan } of entries) {
    if (plan.isSelectable && !plan.needsOptIn) {
      next.delete(id);
    }
  }
  return next;
};

/** The toggled ids after unticking every selected analysis, retries included. */
export const clearAllAnalyses = (
  entries: readonly AnalysisSelectionEntry[],
  toggled: ReadonlySet<string>,
): ReadonlySet<string> => {
  const next = new Set(toggled);
  for (const { id, plan } of entries) {
    if (plan.isSelectable && isAnalysisSelected(plan, toggled.has(id))) {
      // Flip back to the unselected state: toggled for a default-on cell, not for an opt-in one.
      if (plan.needsOptIn) {
        next.delete(id);
      } else {
        next.add(id);
      }
    }
  }
  return next;
};
